import { cloneElement, useMemo, useState } from "react";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Brain,
  ClipboardCheck,
  Copy,
  HeartHandshake,
  LoaderCircle,
  LockKeyhole,
  Sparkles,
} from "lucide-react";
import { PageShell } from "../components/PageShell";
import WellbeingTools from "../components/WellbeingTools";
import { aiTools, browserToolHref, browserTools } from "../toolCatalog";

const apiEndpoint = import.meta.env.DEV
  ? "http://127.0.0.1:5001/drneerjagidwani/asia-south1/mentalHealthTool"
  : "/api/mental-health-tools";

const toolIcons = {
  "appointment-note": ClipboardCheck,
  "support-message": HeartHandshake,
  "reflection-guide": Brain,
};

export function ToolsHubPage() {
  return (
    <PageShell>
      <main className="tool-page" id="main">
        <header className="tool-page__hero">
          <p>Private tools</p>
          <h1>Choose what would help right now.</h1>
          <p>
            Some tools work only in your browser. Three use AI for a short, defined task.
            Each one tells you what leaves your device before you begin.
          </p>
          <a className="button button--ink" href="/tools/appointment-note">Prepare for a visit <ArrowRight size={17} aria-hidden="true" /></a>
        </header>

        <section className="tool-index" aria-labelledby="ai-tools-heading">
          <header className="tool-index__heading">
            <h2 id="ai-tools-heading">Tools that shape your words</h2>
            <p>GPT-5 nano processes one request, then the website discards it.</p>
          </header>
          <div className="tool-index__ai-list">
            {aiTools.map((tool) => {
              const Icon = toolIcons[tool.id];
              return (
                <a className={`tool-index__ai tool-index__ai--${tool.tone}`} href={tool.path} key={tool.id}>
                  <Icon size={26} aria-hidden="true" />
                  <span><strong>{tool.title}</strong><small>{tool.description}</small></span>
                  <span className="tool-index__action">{tool.action} <ArrowRight size={17} aria-hidden="true" /></span>
                </a>
              );
            })}
          </div>
        </section>

        <section className="tool-index tool-index--browser" aria-labelledby="browser-tools-heading">
          <header className="tool-index__heading">
            <h2 id="browser-tools-heading">Tools that stay on your device</h2>
            <p>No AI request. No account. Your text stays in this browser.</p>
          </header>
          <div className="tool-index__browser-list">
            {browserTools.map((tool) => (
              <a href={browserToolHref(tool.id)} key={tool.id}>
                <span><strong>{tool.title}</strong><small>{tool.description}</small></span>
                <span>{tool.action} <ArrowRight size={16} aria-hidden="true" /></span>
              </a>
            ))}
          </div>
        </section>

        <aside className="tool-boundary">
          <LockKeyhole size={22} aria-hidden="true" />
          <div>
            <h2>The boundary is part of the tool.</h2>
            <p>These exercises offer education and preparation. They cannot assess a condition, make a diagnosis, prescribe treatment, or respond to an emergency.</p>
          </div>
          <a href="/#support">Urgent support <ArrowRight size={16} aria-hidden="true" /></a>
        </aside>
      </main>
    </PageShell>
  );
}

export function AIToolPage({ toolId }) {
  const tool = aiTools.find((item) => item.id === toolId);
  if (!tool) return <NotFoundTool />;

  const Icon = toolIcons[tool.id];
  return (
    <PageShell>
      <main className={`ai-tool-page ai-tool-page--${tool.tone}`} id="main">
        <a className="page-back-link" href="/tools"><ArrowLeft size={16} aria-hidden="true" /> All tools</a>
        <header className="ai-tool-page__hero">
          <Icon size={32} aria-hidden="true" />
          <div>
            <p>Private request · GPT-5 nano</p>
            <h1>{tool.title}</h1>
            <p>{tool.description}</p>
          </div>
        </header>
        <AIForm tool={tool} />
        <aside className="ai-disclosure">
          <LockKeyhole size={21} aria-hidden="true" />
          <div>
            <strong>What happens to my text?</strong>
            <p>The secure server sends it to OpenAI for this response with storage disabled. This website does not write it to Firebase or your browser. Avoid names, phone numbers, addresses, and other identifying details.</p>
          </div>
        </aside>
      </main>
    </PageShell>
  );
}

export function BrowserToolPage({ toolId }) {
  if (!browserTools.some((tool) => tool.id === toolId)) return <NotFoundTool />;
  return (
    <PageShell section="Tools">
      <main className="browser-tool-page" id="main">
        <WellbeingTools initialTool={toolId} standalone />
      </main>
    </PageShell>
  );
}

function AIForm({ tool }) {
  const [values, setValues] = useState(initialValues[tool.id]);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [copyStatus, setCopyStatus] = useState("idle");
  const resultText = useMemo(() => formatResult(payload?.result), [payload]);

  const update = (field, value) => {
    setValues((current) => ({ ...current, [field]: value }));
    if (status === "error") {
      setStatus("idle");
      setError("");
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!hasRequiredInput(tool.id, values)) {
      setStatus("error");
      setError("Add the main detail before asking the tool to help.");
      return;
    }
    setStatus("loading");
    setError("");
    setPayload(null);
    try {
      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tool: tool.id, inputs: values }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || "The tool could not complete this request.");
      setPayload(body);
      setStatus("success");
    } catch (requestError) {
      setStatus("error");
      setError(requestError.message || "The tool is unavailable. Your text was not saved.");
    }
  };

  const copy = async () => {
    setCopyStatus("loading");
    try {
      await navigator.clipboard.writeText(resultText);
      setCopyStatus("success");
      window.setTimeout(() => setCopyStatus("idle"), 2200);
    } catch {
      setCopyStatus("error");
    }
  };

  return (
    <section className="ai-workbench" aria-labelledby="workbench-title">
      <form onSubmit={submit} noValidate>
        <header>
          <p><Sparkles size={17} aria-hidden="true" /> One bounded task</p>
          <h2 id="workbench-title">{tool.shortTitle}</h2>
        </header>
        {tool.id === "appointment-note" && <AppointmentFields values={values} update={update} invalid={status === "error"} />}
        {tool.id === "support-message" && <SupportFields values={values} update={update} invalid={status === "error"} />}
        {tool.id === "reflection-guide" && <ReflectionFields values={values} update={update} invalid={status === "error"} />}
        <p className={`form-message form-message--${status}`} role={status === "error" ? "alert" : undefined} aria-live="polite">
          {error || (status === "loading" ? "Shaping a short response…" : "Remove identifying details before you continue.")}
        </p>
        <button className="button button--ink" type="submit" disabled={status === "loading"} data-state={status}>
          {status === "loading" ? <LoaderCircle className="spin" size={18} aria-hidden="true" /> : <Sparkles size={18} aria-hidden="true" />}
          {status === "loading" ? "Working…" : tool.action}
        </button>
      </form>

      <div className="ai-output" aria-live="polite" aria-busy={status === "loading"}>
        {!payload && status !== "loading" && (
          <div className="ai-output__empty">
            <Sparkles size={25} aria-hidden="true" />
            <h2>Your draft will appear here.</h2>
            <p>Read it, change it, and keep only the words that fit your situation.</p>
          </div>
        )}
        {status === "loading" && <OutputSkeleton />}
        {payload && (
          <article className={payload.urgent ? "ai-result ai-result--urgent" : "ai-result"}>
            {payload.urgent && <AlertTriangle size={25} aria-hidden="true" />}
            <p>{payload.urgent ? "Immediate support" : "A draft to review"}</p>
            <h2>{payload.result.heading}</h2>
            <p>{payload.result.intro}</p>
            {payload.result.sections.map((section) => (
              <section key={section.title}><h3>{section.title}</h3><p>{section.body}</p></section>
            ))}
            <section><h3>Next step</h3><p>{payload.result.nextStep}</p></section>
            <aside>{payload.result.caution}</aside>
            {payload.urgent ? (
              <div className="tool-actions">
                <a className="button button--ink" href="tel:112">Call 112</a>
                <a className="button button--quiet" href="tel:14416">Call 14416</a>
              </div>
            ) : (
              <button className="button button--quiet" type="button" onClick={copy} disabled={copyStatus === "loading"} data-state={copyStatus}>
                <Copy size={17} aria-hidden="true" /> {copyStatus === "success" ? "Copied" : copyStatus === "error" ? "Copy failed" : "Copy draft"}
              </button>
            )}
          </article>
        )}
      </div>
    </section>
  );
}

function AppointmentFields({ values, update, invalid }) {
  return <>
    <Field id="concerns" label="What would you like to discuss?" help="Describe changes, daily-life effects, or concerns in your own words." required invalid={invalid && !values.concerns.trim()}>
      <textarea id="concerns" value={values.concerns} onChange={(event) => update("concerns", event.target.value)} maxLength={1600} placeholder="For example: sleep changed around exam week…" />
    </Field>
    <Field id="timeline" label="Timing or pattern" help="Optional · Include when it began, frequency, and anything that changed.">
      <textarea id="timeline" value={values.timeline} onChange={(event) => update("timeline", event.target.value)} maxLength={800} placeholder="For example: most mornings for two weeks…" />
    </Field>
    <Field id="questions" label="Questions you want to remember" help="Optional · Add questions for the clinician.">
      <textarea id="questions" value={values.questions} onChange={(event) => update("questions", event.target.value)} maxLength={800} placeholder="For example: what should I track before the next visit?" />
    </Field>
  </>;
}

function SupportFields({ values, update, invalid }) {
  return <>
    <Field id="relationship" label="Who are you checking in with?" help="Choose the closest relationship.">
      <select id="relationship" value={values.relationship} onChange={(event) => update("relationship", event.target.value)}>
        <option value="friend">Friend</option><option value="partner">Partner</option><option value="family">Family member</option><option value="colleague">Colleague</option><option value="other">Someone else</option>
      </select>
    </Field>
    <Field id="situation" label="What have you noticed?" help="Describe the change without names or identifying details." required invalid={invalid && !values.situation.trim()}>
      <textarea id="situation" value={values.situation} onChange={(event) => update("situation", event.target.value)} maxLength={1400} placeholder="For example: they have been quieter and stopped joining our evening walks…" />
    </Field>
    <Field id="tone" label="How should the message sound?" help="You can edit every word after the draft.">
      <select id="tone" value={values.tone} onChange={(event) => update("tone", event.target.value)}>
        <option value="gentle">Gentle</option><option value="direct">Clear and direct</option><option value="brief">Brief</option>
      </select>
    </Field>
  </>;
}

function ReflectionFields({ values, update, invalid }) {
  return <>
    <Field id="situation" label="What happened?" help="Use one recent, specific situation." required invalid={invalid && !values.situation.trim()}>
      <textarea id="situation" value={values.situation} onChange={(event) => update("situation", event.target.value)} maxLength={1000} placeholder="For example: I sent a message this morning and have not received a reply…" />
    </Field>
    <Field id="thought" label="What thought showed up?" help="Optional · Keep the words that came to mind.">
      <textarea id="thought" value={values.thought} onChange={(event) => update("thought", event.target.value)} maxLength={800} placeholder="For example: I must have said something wrong…" />
    </Field>
    <Field id="feeling" label="What did you feel?" help="Optional · A few words are enough.">
      <input id="feeling" value={values.feeling} onChange={(event) => update("feeling", event.target.value)} maxLength={400} placeholder="Worried, embarrassed, tense" />
    </Field>
    <Field id="aim" label="What would help next?" help="Choose one aim for this reflection.">
      <select id="aim" value={values.aim} onChange={(event) => update("aim", event.target.value)}>
        <option value="understand">Understand the thought</option><option value="prepare">Prepare for a conversation</option><option value="settle">Find a small grounding step</option>
      </select>
    </Field>
  </>;
}

function Field({ id, label, help, required = false, invalid = false, children }) {
  const control = cloneElement(children, {
    "aria-describedby": `${id}-help`,
    "aria-invalid": invalid || undefined,
    "aria-required": required || undefined,
    required,
  });
  return (
    <div className="ai-field">
      <label className="field-label" htmlFor={id}>{label}{required && <span>Required</span>}</label>
      {control}
      <p id={`${id}-help`} className={invalid ? "field-help field-help--error" : "field-help"}>{invalid ? "This field needs a short description before the tool can continue." : help}</p>
    </div>
  );
}

function OutputSkeleton() {
  return <div className="ai-output__skeleton" aria-label="Preparing a draft"><span /><span /><span /><span /></div>;
}

function NotFoundTool() {
  return <PageShell><main className="not-found-page"><h1>That tool is not here.</h1><a href="/tools">Browse all tools <ArrowRight size={17} aria-hidden="true" /></a></main></PageShell>;
}

const initialValues = {
  "appointment-note": { concerns: "", timeline: "", questions: "" },
  "support-message": { relationship: "friend", situation: "", tone: "gentle" },
  "reflection-guide": { situation: "", thought: "", feeling: "", aim: "understand" },
};

function hasRequiredInput(toolId, values) {
  return toolId === "appointment-note" ? Boolean(values.concerns.trim())
    : toolId === "support-message" ? Boolean(values.situation.trim())
      : Boolean(values.situation.trim());
}

function formatResult(result) {
  if (!result) return "";
  return [result.heading, result.intro, ...result.sections.flatMap((section) => [section.title, section.body]), "Next step", result.nextStep, result.caution].join("\n\n");
}
